import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Input, Modal, Select, Spin, Tag, Tooltip, Typography,
} from '@arco-design/web-react';
import {
  IconCheck, IconCheckCircle, IconClockCircle, IconDownload, IconEye,
  IconMessage, IconRefresh, IconSend, IconShareInternal, IconUpload,
} from '@arco-design/web-react/icon';
import fileDoc from 'jukun-ui-standard/dist-css/assets/file-icons/file-doc.svg';
import filePdf from 'jukun-ui-standard/dist-css/assets/file-icons/file-pdf.svg';
import fileXls from 'jukun-ui-standard/dist-css/assets/file-icons/file-xls.svg';
import fileOther from 'jukun-ui-standard/dist-css/assets/file-icons/file-other.svg';
import emptyListIllustration from 'jukun-ui-standard/dist-css/assets/states/enterprise/state-empty-list.webp';
import { ApiError, errorMessage, isAmbiguousRequestFailure } from '../../../common/api/httpClient';
import type { PortalApplication, PortalIdentity } from '../../../model/portalModels';
import { INSTANCE_COMMENTS_PAGE_SIZE, MY_TASKS_PAGE_SIZE, myTasksApi } from '../api/myTasksApi';
import { workItemActionErrorMessage } from '../runtime/workItemActionError';
import { sourceSaveErrorMessage } from '../../../common/api/sourceApplicationError';
import { buildMyTasksRoute } from '../runtime/myTasksRoute';
import type {
  PortalMyTaskCounts, PortalTaskAttachment, PortalTaskGroupKey, PortalTransferTarget,
  PortalWorkItemAction, PortalTaskDetail, PortalWorkItemListItem,
  PortalUploadedAttachment,
  PortalTaskComment, PageResult,
} from '../model/myTasks';
import { formatShanghai } from '../model/myTasks';
import {
  TenantApplicationEditPage,
  type TenantApplicationEditHandle,
} from './TenantApplicationEditPage';
import {
  WorkItemFormHost,
  type WorkItemFormRuntimeState,
} from './WorkItemFormHost';
import { isBuiltInTenantApplication } from '../runtime/workItemRuntime';
import { workItemActionLabel } from '../runtime/workItemActionPresentation';
import { CcReadCoordinator } from '../runtime/ccReadCoordinator';
import {
  WorkItemActionGate,
  type WorkItemActionValidationGrant,
  workflowActionNeedsDialog,
} from '../runtime/workItemActionGate';
import { usePortalOverlay } from '../../../components/PortalOverlayProvider';
import { InitiationViewFormHost } from './InitiationViewFormHost';
import { StandardAttachment } from '../../../components/standard';
import type { FormSessionToken } from '@jukun/platform-sdk/shell';
import { usePortalFormSessionManager, usePortalFormSessionSnapshot } from '../../../runtime/formSessionContext';
import { hasLocalWorkItemSave, type PreparedTaskSelection, type TaskNavigate } from '../runtime/taskFormSelection';
import MobileTasksView from '../../../mobile/views/MobileTasksView';
import MobileTaskDetailView from '../../../mobile/views/MobileTaskDetailView';
import MobileTaskActionPage from '../../../mobile/views/MobileTaskActionPage';
import MobileTaskCommentSheet from '../../../mobile/views/MobileTaskCommentSheet';
import { taskModel } from '../../../mobile/tasks/taskModels';

const { Text } = Typography;
type DetailTabKey = 'form' | 'approval' | 'comment';
type PendingWorkflowAction = Readonly<{
  action: PortalWorkItemAction;
  taskId: string;
  detailTaskId: string;
  taskVersion: number;
  mountId: string;
  formValidationPolicy: 'REQUIRED' | 'SKIP';
  validationGrant?: WorkItemActionValidationGrant;
  opensDialog: boolean;
}>;
type ActionAttachment = PortalUploadedAttachment & Readonly<{ previewUrl: string; file: File }>;

const fileIconAssets = { pdf: filePdf, word: fileDoc, excel: fileXls, other: fileOther };
const emptyCounts: PortalMyTaskCounts = {
  todo: 0, urgent: 0, done: 0, cc: 0, initiated: 0,
};

const taskGroups: Array<{ key: PortalTaskGroupKey; label: string }> = [
  { key: 'todo', label: '待办' },
  { key: 'done', label: '已办' },
  { key: 'cc', label: '抄送' },
  { key: 'initiated', label: '发起' },
];

type TraceIndicatorTone = 'success' | 'warning' | 'danger' | 'neutral' | 'default';

export function traceIndicatorTone(action: string): TraceIndicatorTone {
  const normalized = action.trim().toUpperCase();
  if (['SUBMIT', 'APPROVE'].includes(normalized)
      || /提交|通过|同意|批准/.test(action)) return 'success';
  if (['RECALL', 'RETURN'].includes(normalized)
      || /撤回|驳回|退回/.test(action)) return 'warning';
  if (['END', 'TERMINATE'].includes(normalized)
      || /结束|终止|拒绝/.test(action)) return 'danger';
  if (['CC', 'CC_RECEIVED'].includes(normalized)
      || /抄送/.test(action)) return 'neutral';
  return 'default';
}

function traceIndicatorColor(tone: TraceIndicatorTone, status: string) {
  if (tone === 'success') return 'green';
  if (tone === 'warning') return 'orange';
  if (tone === 'danger') return 'red';
  if (tone === 'neutral') return 'gray';
  return status === 'active' ? 'arcoblue' : 'gray';
}

function traceItemStateLabel(state: string) {
  if (state === 'ACTIVE') return '执行中';
  if (state === 'COMPLETED') return '已完成';
  if (state === 'RECALLED') return '已撤回';
  if (state === 'CANCELLED') return '已取消';
  return state;
}

function traceNodeLabel(node: PortalTaskDetail['traceNodes'][number]) {
  if (node.nodeRole === 'CC') return '已抄送';
  if (node.status === 'active') return '处理中';
  return node.records.at(-1)?.action || traceItemStateLabel(node.itemState);
}

function visibleTraceRecords(node: PortalTaskDetail['traceNodes'][number]) {
  if (node.nodeRole !== 'CC') {
    return node.records.map(record => ({ record, read: false }));
  }
  const readParticipantIds = new Set(node.records
    .filter(record => record.action === '已读' && record.participantId)
    .map(record => record.participantId));
  const records: Array<{ record: typeof node.records[number]; read: boolean }> = [];
  const ccRecordIndexes = new Map<string, number>();
  for (const record of node.records) {
    const isCcEvent = record.action === '收到抄送' || record.action === '已读';
    if (!isCcEvent || !record.participantId) {
      records.push({ record, read: false });
      continue;
    }
    const read = readParticipantIds.has(record.participantId);
    const index = ccRecordIndexes.get(record.participantId);
    if (index === undefined) {
      ccRecordIndexes.set(record.participantId, records.length);
      records.push({ record, read });
    } else if (record.action === '收到抄送'
        && records[index].record.action === '已读') {
      records[index] = { record, read };
    }
  }
  return records;
}

function taskDetailTabs(category: PortalTaskGroupKey): Array<{ key: DetailTabKey; label: string }> {
  return [
    { key: 'form', label: category === 'initiated' ? '流程表单' : '表单详情' },
    { key: 'approval', label: '流程详情' },
    { key: 'comment', label: '评论' },
  ];
}

function taskGroupIcon(key: PortalTaskGroupKey) {
  if (key === 'done') return <IconCheckCircle />;
  if (key === 'cc') return <IconShareInternal />;
  if (key === 'initiated') return <IconSend />;
  return <IconClockCircle />;
}

function taskActionIcon(action: PortalWorkItemAction) {
  if (action.actionCode === 'SAVE') return <IconCheck />;
  if (action.actionCode === 'APPROVE') return <IconCheck />;
  if (action.actionCode === 'TRANSFER') return <IconShareInternal />;
  return <IconRefresh />;
}

function taskCardTime(task: PortalWorkItemListItem) {
  if (task.category === 'done') return `处理时间：${formatShanghai(task.updatedAt)}`;
  if (task.category === 'initiated') return `发起时间：${formatShanghai(task.receivedAt)}`;
  return `抵达时间：${formatShanghai(task.receivedAt)}`;
}

function sourceSystemName(sourceSystem: string) {
  return sourceSystem.endsWith('系统') ? sourceSystem : `${sourceSystem}系统`;
}

function UserChip(props: { name: string; avatarUrl?: string; meta?: string }) {
  const displayName = props.name.trim() || '-';
  return <span className="ui-user ui-user--compact portal-task-user">
    {props.avatarUrl
      ? <img className="ui-user__avatar" src={props.avatarUrl} alt={displayName} />
      : <span className="ui-user__avatar portal-task-user__avatar-fallback" aria-hidden="true">
          {displayName.slice(0, 1)}
        </span>}
    <span className="ui-user__body">
      <span className="ui-user__name">{props.name}</span>
      {props.meta ? <span className="ui-user__meta">{props.meta}</span> : null}
    </span>
  </span>;
}

function AttachmentActions(props: { attachment: PortalTaskAttachment }) {
  const unavailable = '当前附件记录未提供文件预览或下载地址';
  return <span className="ui-attachment__actions">
    <Tooltip content={unavailable}>
      <Button size="mini" icon={<IconEye />} aria-label={`预览 ${props.attachment.name}`} disabled />
    </Tooltip>
    <Tooltip content={unavailable}>
      <Button size="mini" icon={<IconDownload />} aria-label={`下载 ${props.attachment.name}`} disabled />
    </Tooltip>
  </span>;
}

function AttachmentBox(props: { attachments: PortalTaskAttachment[] }) {
  return <div className="ui-attachment-box portal-form-attachments">
    <div className="ui-attachment-box__head"><strong>附件</strong><span>{props.attachments.length} 个文件</span></div>
    <div className="ui-attachment-box__list">
      {props.attachments.map(attachment => (
        <div className="ui-attachment" key={attachment.fileAssetPublicId}>
          <div className="ui-attachment__meta">
            <div className={`ui-file-icon ui-file-icon--${attachment.type}`}>
              <img src={fileIconAssets[attachment.type]} alt="" />
            </div>
            <div className="ui-attachment__meta-content">
              <strong className="ui-attachment__name">{attachment.name}</strong>
              <span className="ui-attachment__info">{attachment.type.toUpperCase()} · {attachment.size}</span>
            </div>
          </div>
          <AttachmentActions attachment={attachment} />
        </div>
      ))}
    </div>
  </div>;
}

export function MyTasksWorkspace(props: {
  initialGroup?: PortalTaskGroupKey;
  initialTaskId?: string;
  applications: PortalApplication[];
  identity: PortalIdentity;
  preparedSelection?: PreparedTaskSelection;
  navigationLoadingMessage?: string;
  onNavigate: TaskNavigate;
  onSessionExpired: () => void;
  mobile?: boolean;
}) {
  const { message } = usePortalOverlay();
  const sessionManager = usePortalFormSessionManager();
  const formSession = usePortalFormSessionSnapshot().activeSession;
  const legacyInitiatedRoute = !props.initialGroup
    && (props.initialTaskId?.startsWith('initiated/') ?? false);
  const initialGroup = props.initialGroup
    ?? (legacyInitiatedRoute ? 'initiated' : 'todo');
  const initialItemId = legacyInitiatedRoute
    ? props.initialTaskId?.slice('initiated/'.length) : props.initialTaskId;
  const [activeGroup, setActiveGroup] = useState<PortalTaskGroupKey>(
    initialGroup);
  const [counts, setCounts] = useState<PortalMyTaskCounts>(emptyCounts);
  const [tasks, setTasks] = useState<PortalWorkItemListItem[]>([]);
  const [activeTaskId, setActiveTaskId] = useState(initialItemId ?? '');
  const [detail, setDetail] = useState<PortalTaskDetail | undefined>(props.preparedSelection?.detail);
  const [displayedSelection, setDisplayedSelection] = useState(props.preparedSelection);
  const [processDefinitionName, setProcessDefinitionName] = useState<string>();
  const [processDefinitionNames, setProcessDefinitionNames] = useState<string[]>([]);
  const [keyword, setKeyword] = useState('');
  const keywordRef = useRef(keyword);
  keywordRef.current = keyword;
  const [mobileCommentIntent, setMobileCommentIntent] = useState<number>();
  const mobileCommentIntentRef = useRef<number | undefined>(undefined);
  const nextMobileCommentIntentRef = useRef(0);
  const [readBusy, setReadBusy] = useState(false);
  const readAttemptRef = useRef(false);
  const [activeTab, setActiveTab] = useState<DetailTabKey>('form');
  const [commentValue, setCommentValue] = useState('');
  const [replyTargetId, setReplyTargetId] = useState<string>();
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [instanceComments, setInstanceComments] = useState<PageResult<PortalTaskComment>>();
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState<string>();
  const commentRequestRef = useRef<AbortController | undefined>(undefined);
  const commentSubmissionRef = useRef<{
    taskId: string; content: string; parentCommentId?: string; key: string;
  } | undefined>(undefined);
  const [listLoading, setListLoading] = useState(false);
  const commentExecutingRef = useRef<object | undefined>(undefined);
  const [listPage, setListPage] = useState(0);
  const [listTotal, setListTotal] = useState(0);
  const [detailLoading, setDetailLoading] = useState(false);
  const [listError, setListError] = useState<string>();
  const [pendingAction, setPendingAction] = useState<PendingWorkflowAction>();
  const [actionOpinion, setActionOpinion] = useState('');
  const [targetUserId, setTargetUserId] = useState<string>();
  const [transferTargets, setTransferTargets] = useState<PortalTransferTarget[]>([]);
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [actionAttachments, setActionAttachments] =
    useState<ActionAttachment[]>([]);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const attachmentUploadRef = useRef<PendingWorkflowAction | undefined>(undefined);
  const actionExecutingRef = useRef<PendingWorkflowAction | undefined>(undefined);
  const transferQueryRef = useRef(0);
  const actionAttachmentsRef = useRef(actionAttachments);
  actionAttachmentsRef.current = actionAttachments;
  const attachmentInputRef = useRef<HTMLInputElement | null>(null);
  const detailBodyRef = useRef<HTMLDivElement | null>(null);
  const listQueryGenerationRef = useRef(0);
  const loadingNextPageRef = useRef(false);
  const formRef = useRef<HTMLElement | null>(null);
  const approvalRef = useRef<HTMLElement | null>(null);
  const commentRef = useRef<HTMLElement | null>(null);
  const tenantApplicationFormRef = useRef<TenantApplicationEditHandle | null>(null);
  const [workItemRuntime, setWorkItemRuntime] = useState<WorkItemFormRuntimeState>();
  const [builtInFormRuntime, setBuiltInFormRuntime] = useState<{
    taskId: string;
    ready: boolean;
  }>();
  const actionGateRef = useRef<{ key: string; gate: WorkItemActionGate } | undefined>(undefined);
  const ccReadCoordinatorRef = useRef(new CcReadCoordinator());
  const activeTaskIdRef = useRef(activeTaskId);
  const initialTaskIdRef = useRef(initialItemId);
  initialTaskIdRef.current = initialItemId;
  const detailTaskIdRef = useRef<string | undefined>(props.preparedSelection?.itemId);
  const detailRequestGenerationRef = useRef(0);
  const detailRequestAbortRef = useRef<AbortController | undefined>(undefined);
  const workItemRuntimeRef = useRef<WorkItemFormRuntimeState | undefined>(undefined);
  const pendingActionRef = useRef<PendingWorkflowAction | undefined>(undefined);
  const actionOpeningRef = useRef<object | undefined>(undefined);
  const retainedActionInputRef = useRef<{ taskId: string; code: string; opinion: string; target?: string } | undefined>(undefined);
  const activeTaskIdentityRef = useRef<{
    taskId: string;
    taskVersion: number;
    detailTaskId: string;
  } | undefined>(undefined);
  const preparedRef = useRef(props.preparedSelection);
  preparedRef.current = props.preparedSelection;
  const acceptedRouteRef = useRef<string | undefined>(undefined);
  const localSaveAttemptRef = useRef<{
    sessionId: string; operationId: string; traceId: string; running?: Promise<void>;
  } | undefined>(undefined);

  const disposeActionGate = useCallback(() => {
    actionGateRef.current?.gate.dispose();
    actionGateRef.current = undefined;
  }, []);

  const setPendingWorkflowAction = useCallback((next?: PendingWorkflowAction) => {
    pendingActionRef.current = next;
    setPendingAction(next);
  }, []);

  const clearPendingWorkflowAction = useCallback(() => {
    pendingActionRef.current = undefined;
    setPendingAction(undefined);
    setActionOpinion('');
    setTargetUserId(undefined);
    setTransferTargets([]);
    setActionAttachments(current => {
      current.forEach(attachment => URL.revokeObjectURL(attachment.previewUrl));
      return [];
    });
    if (attachmentInputRef.current) attachmentInputRef.current.value = '';
    setAttachmentUploading(false);
    attachmentUploadRef.current = undefined;
    transferQueryRef.current += 1;
    setActionSubmitting(false);
  }, []);

  const clearTaskBoundRuntime = useCallback(() => {
    actionOpeningRef.current = undefined;
    disposeActionGate();
    clearPendingWorkflowAction();
    tenantApplicationFormRef.current = null;
    workItemRuntimeRef.current = undefined;
    setWorkItemRuntime(undefined);
    setBuiltInFormRuntime(undefined);
  }, [clearPendingWorkflowAction, disposeActionGate]);

  const invalidateDetailRequest = useCallback((clearState = true) => {
    detailRequestGenerationRef.current += 1;
    detailRequestAbortRef.current?.abort();
    detailRequestAbortRef.current = undefined;
    detailTaskIdRef.current = undefined;
    clearTaskBoundRuntime();
    if (clearState) {
      setDetail(undefined);
      setDetailLoading(false);
    }
  }, [clearTaskBoundRuntime]);

  const activateTaskId = useCallback((taskId: string) => {
    if (!taskId || activeTaskIdRef.current === taskId) return;
    void props.onNavigate(buildMyTasksRoute(initialGroup, taskId));
  }, [initialGroup, props.onNavigate]);

  const handleWorkItemRuntimeChange = useCallback((next: WorkItemFormRuntimeState) => {
    if (next.taskId !== activeTaskIdRef.current
        || next.taskId !== detailTaskIdRef.current) return;
    disposeActionGate();
    workItemRuntimeRef.current = next;
    setWorkItemRuntime(next);
  }, [disposeActionGate]);

  const handleBuiltInRuntimeChange = useCallback((taskId: string, ready: boolean) => {
    if (!taskId
        || taskId !== activeTaskIdRef.current
        || taskId !== detailTaskIdRef.current) return;
    disposeActionGate();
    setBuiltInFormRuntime({ taskId, ready });
  }, [disposeActionGate]);

  const isCurrentActionIdentity = useCallback((intent: PendingWorkflowAction) => {
    const active = activeTaskIdentityRef.current;
    const runtimeState = workItemRuntimeRef.current;
    return activeTaskIdRef.current === intent.taskId
      && detailTaskIdRef.current === intent.detailTaskId
      && active?.taskId === intent.taskId
      && active.detailTaskId === intent.detailTaskId
      && active.taskVersion === intent.taskVersion
      && runtimeState?.taskId === intent.taskId
      && runtimeState.status === 'ready'
      && runtimeState.mountId === intent.mountId
      && !!runtimeState.token
      && sessionManager.getSnapshot().activeSession?.status === 'READY'
      && sessionManager.getSnapshot().activeSession?.token.sessionId === runtimeState.token.sessionId;
  }, [sessionManager]);

  const loadCounts = async () => {
    try { setCounts(await myTasksApi.counts()); }
    catch (cause) { message.error(errorMessage(cause)); }
  };

  const loadList = async (
    pageNumber = 1, append = false,
    group = activeGroup, currentProcessDefinitionName = processDefinitionName,
  ) => {
    if (append && loadingNextPageRef.current) return [];
    const generation = append
      ? listQueryGenerationRef.current
      : ++listQueryGenerationRef.current;
    if (append) loadingNextPageRef.current = true;
    else {
      setTasks([]); setListPage(0); setListTotal(0);
      setListError(undefined);
      loadingNextPageRef.current = false;
    }
    setListLoading(true); setListError(undefined);
    try {
      const page = await myTasksApi.list({
        category: group,
        processDefinitionName: currentProcessDefinitionName, keyword: keywordRef.current || undefined,
        page: pageNumber, pageSize: MY_TASKS_PAGE_SIZE,
      });
      if (generation !== listQueryGenerationRef.current) return [];
      setListPage(page.page); setListTotal(page.total);
      if (append) {
        setTasks(current => {
          const existing = new Set(current.map(task => task.taskId));
          return [...current, ...page.items.filter(task => !existing.has(task.taskId))];
        });
      } else {
        setTasks(page.items);
        const currentTaskId = activeTaskIdRef.current;
        const keepCurrent = !!detailTaskIdRef.current || page.items.some(task => task.taskId === currentTaskId)
          || Boolean(initialItemId && currentTaskId === initialItemId);
        if (!props.mobile) activateTaskId(keepCurrent ? currentTaskId : page.items[0]?.taskId ?? '');
      }
      return page.items;
    } catch (cause) {
      if (generation !== listQueryGenerationRef.current) return [];
      if (append) message.error(errorMessage(cause));
      else {
        setListError(errorMessage(cause)); setTasks([]);
      }
      return [];
    } finally {
      if (append && generation === listQueryGenerationRef.current) loadingNextPageRef.current = false;
      if (generation === listQueryGenerationRef.current) setListLoading(false);
    }
  };

  const loadDetail = useCallback(async (
    taskId: string,
    syncGroup = false,
    expectedAction?: PendingWorkflowAction,
  ) => {
    if (expectedAction && !isCurrentActionIdentity(expectedAction)) return undefined;
    if (!taskId || taskId !== activeTaskIdRef.current) return undefined;
    const allowed = await props.onNavigate(buildMyTasksRoute(activeGroup, taskId),
      { force: true, replace: true, refresh: true });
    if (!allowed) return undefined;
    if (syncGroup && preparedRef.current?.itemId === taskId) setActiveGroup(preparedRef.current.detail.summary.category);
    return preparedRef.current?.itemId === taskId ? preparedRef.current.detail : undefined;
  }, [activeGroup, props.onNavigate, isCurrentActionIdentity]);

  useEffect(() => { void loadCounts(); }, []);

  useEffect(() => {
    let active = true;
    setProcessDefinitionNames([]);
    void myTasksApi.processDefinitionNames(activeGroup).then(names => {
      if (active) setProcessDefinitionNames(names);
    }).catch(cause => {
      if (active) message.error(errorMessage(cause));
    });
    return () => { active = false; };
  }, [activeGroup, message]);

  useEffect(() => {
    const timer = window.setTimeout(() => void loadList(1, false), 240);
    return () => window.clearTimeout(timer);
  }, [activeGroup, processDefinitionName, keyword]);

  useLayoutEffect(() => {
    // Invalidate immediately; a response arriving during the debounce belongs to the old filter.
    listQueryGenerationRef.current += 1;
    loadingNextPageRef.current = false;
    setTasks([]); setListPage(0); setListTotal(0); setListError(undefined);
    setListLoading(true);
  }, [activeGroup, processDefinitionName, keyword]);

  useLayoutEffect(() => {
    const route = buildMyTasksRoute(initialGroup, initialItemId);
    const prepared = props.preparedSelection;
    if (initialItemId && (!prepared || prepared.itemId !== initialItemId || prepared.group !== initialGroup)) {
      if (acceptedRouteRef.current === route) return;
      acceptedRouteRef.current = route;
      setDetailLoading(!detailTaskIdRef.current);
      void props.onNavigate(route, { replace: true }).finally(() => setDetailLoading(false));
      return;
    }
    acceptedRouteRef.current = route;
    const changing = activeTaskIdRef.current !== (initialItemId ?? '');
    if (changing) {
      commentRequestRef.current?.abort(); commentRequestRef.current = undefined;
      commentSubmissionRef.current = undefined;
      setInstanceComments(undefined); setCommentsError(undefined);
      setReplyTargetId(undefined); setCommentValue(''); setActiveTab('form'); setMobileCommentIntent(undefined);
      mobileCommentIntentRef.current = undefined;
      clearTaskBoundRuntime(); localSaveAttemptRef.current = undefined;
      retainedActionInputRef.current = undefined;
      commentExecutingRef.current = undefined; setCommentSubmitting(false);
      detailBodyRef.current?.scrollTo({ top: 0 });
    }
    setActiveGroup(prepared?.detail.summary.category ?? initialGroup);
    activeTaskIdRef.current = initialItemId ?? '';
    setActiveTaskId(initialItemId ?? '');
    detailTaskIdRef.current = prepared?.itemId;
    setDisplayedSelection(prepared);
    setDetail(prepared?.detail);
    setDetailLoading(false);
  }, [clearTaskBoundRuntime, initialGroup, initialItemId, props.preparedSelection, props.onNavigate]);

  useEffect(() => () => {
    detailRequestGenerationRef.current += 1;
    detailRequestAbortRef.current?.abort();
    detailRequestAbortRef.current = undefined;
    disposeActionGate();
    ccReadCoordinatorRef.current.cancel();
    actionAttachmentsRef.current.forEach(attachment => URL.revokeObjectURL(attachment.previewUrl));
    listQueryGenerationRef.current += 1;
  }, [disposeActionGate]);

  const activeDetail = detail?.summary.taskId === activeTaskId ? detail : undefined;
  const activeTask = activeDetail?.summary
    ?? tasks.find(task => task.taskId === activeTaskId);
  const initiatedDetail = activeDetail && 'commentCount' in activeDetail
    ? activeDetail : undefined;
  const visibleComments = initiatedDetail
    ? instanceComments?.items ?? [] : activeDetail?.comments ?? [];
  const commentTotal = initiatedDetail
    ? instanceComments?.total ?? initiatedDetail.commentCount : visibleComments.length;

  const loadInstanceComments = useCallback(async (cardId: string, page = 1) => {
    commentRequestRef.current?.abort();
    const controller = new AbortController();
    commentRequestRef.current = controller;
    setCommentsLoading(true);
    setCommentsError(undefined);
    try {
      const result = await myTasksApi.instanceComments(cardId, page,
        { signal: controller.signal });
      if (commentRequestRef.current === controller
          && activeTaskIdRef.current === cardId) setInstanceComments(result);
    } catch (cause) {
      if (commentRequestRef.current === controller
          && !(cause instanceof DOMException && cause.name === 'AbortError')) {
        setCommentsError(errorMessage(cause));
      }
    } finally {
      if (commentRequestRef.current === controller) {
        commentRequestRef.current = undefined;
        setCommentsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!initiatedDetail) return;
    void loadInstanceComments(initiatedDetail.summary.taskId);
    return () => { commentRequestRef.current?.abort(); };
  }, [initiatedDetail?.summary.taskId, loadInstanceComments]);
  activeTaskIdentityRef.current = activeTask && activeDetail
    ? {
      taskId: activeTask.taskId,
      taskVersion: activeTask.runtimeTaskVersion,
      detailTaskId: activeDetail.summary.taskId,
    }
    : undefined;
  const detailTabs = activeTask ? taskDetailTabs(activeTask.category) : [];
  const hasMoreTasks = tasks.length < listTotal;
  const builtInForm = Boolean(activeDetail?.flowPageContext
    && isBuiltInTenantApplication(activeDetail as Extract<PortalTaskDetail,
      { flowPageContext: object }>));
  const actionHostReady = Boolean(activeTask && activeDetail && !builtInForm
    && activeDetail.summary.taskId === activeTask.taskId
    && workItemRuntime?.taskId === activeTask.taskId
    && workItemRuntime.status === 'ready');
  const builtInLocalSaveReady = Boolean(activeTask && activeDetail && builtInForm
    && builtInFormRuntime?.taskId === activeTask.taskId
    && builtInFormRuntime.ready);
  const externalLocalSaveReady = Boolean(activeTask && activeDetail && !builtInForm
    && hasLocalWorkItemSave(activeDetail)
    && workItemRuntime?.taskId === activeTask.taskId
    && workItemRuntime.status === 'ready'
    && workItemRuntime.saveCapability === 'REQUIRED'
    && workItemRuntime.bridge);
  const sourceBusy = Boolean(formSession?.hostPending || formSession?.state?.busy);
  const sourceUncertain = Boolean(formSession?.uncertain || formSession?.state?.uncertain);
  useEffect(() => {
    if (pendingAction && !isCurrentActionIdentity(pendingAction)) clearPendingWorkflowAction();
  }, [pendingAction, workItemRuntime, activeTask?.runtimeTaskVersion, clearPendingWorkflowAction, isCurrentActionIdentity]);
  const actionDisabled = (action: PortalWorkItemAction) => {
    if (actionSubmitting || sourceBusy || sourceUncertain || pendingAction || attachmentUploading
        || activeTask?.category !== 'todo') return true;
    if (action.operationType === 'SHELL_LOCAL') return action.actionCode !== 'SAVE'
      || !activeDetail || !hasLocalWorkItemSave(activeDetail) || !(builtInForm ? builtInLocalSaveReady : externalLocalSaveReady);
    const allowed = activeDetail?.flowPageContext?.allowedActions.find(candidate => candidate.actionCode === action.actionCode
      && candidate.operationType === action.operationType);
    const policy = ['SUBMIT', 'APPROVE'].includes(action.actionCode) ? 'REQUIRED'
      : ['RETURN', 'RECALL', 'END', 'TRANSFER'].includes(action.actionCode) ? 'SKIP' : undefined;
    return !actionHostReady || !policy || allowed?.formValidationPolicy !== policy;
  };

  const applyCcRead = useCallback((taskId: string) => {
    setTasks(current => current.map(task => task.taskId === taskId
      ? { ...task, unread: false, status: '抄送已读' } : task));
    setDetail(current => current && current.summary.taskId === taskId
      ? { ...current, summary: { ...current.summary, unread: false, status: '抄送已读' } }
      : current);
  }, []);

  useEffect(() => {
    const runtimeState = workItemRuntime;
    if (!activeTask || activeTask.category !== 'cc' || !activeTask.unread
        || !activeDetail || activeDetail.summary.taskId !== activeTask.taskId
        || activeDetail.flowPageContext?.recipientType !== 'CC'
        || activeDetail.flowPageContext.allowedActions.length !== 0
        || runtimeState?.status !== 'ready'
        || runtimeState.taskId !== activeTask.taskId
        || runtimeState.saveCapability !== 'NONE') {
      ccReadCoordinatorRef.current.cancel();
      return undefined;
    }
    const taskId = activeTask.taskId;
    const mountId = runtimeState.mountId;
    ccReadCoordinatorRef.current.schedule({
      taskId,
      mountId,
      isCurrent: () => activeTaskIdRef.current === taskId
        && detailTaskIdRef.current === taskId
        && workItemRuntimeRef.current?.status === 'ready'
        && workItemRuntimeRef.current.taskId === taskId
        && workItemRuntimeRef.current.mountId === mountId,
      markRead: (id, signal) => myTasksApi.markRead(id, { signal }),
      shouldRetry: isAmbiguousRequestFailure,
      onRead: () => {
        applyCcRead(taskId);
        void loadCounts();
        void myTasksApi.detail(taskId, 'work-item').then(value => {
          if (activeTaskIdRef.current !== taskId || detailTaskIdRef.current !== taskId) return;
          setDetail(current => current?.summary.taskId === taskId
            ? { ...current, traceNodes: value.traceNodes } : current);
        }).catch(() => undefined);
      },
    });
    return () => ccReadCoordinatorRef.current.cancel();
  }, [activeDetail, activeTask, applyCcRead, workItemRuntime]);

  const loadNextPage = () => {
    if (listLoading || !hasMoreTasks || listPage < 1) return;
    void loadList(listPage + 1, true);
  };

  const handleTaskListScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const container = event.currentTarget;
    if (container.scrollHeight - container.scrollTop - container.clientHeight <= 80) {
      loadNextPage();
    }
  };

  const selectTask = async (taskId: string) => {
    if (await props.onNavigate(buildMyTasksRoute(activeGroup, taskId))) {
      setActiveTab('form'); detailBodyRef.current?.scrollTo({ top: 0 });
    }
  };

  const selectGroup = async (group: PortalTaskGroupKey) => {
    if (!await props.onNavigate(buildMyTasksRoute(group))) return;
    setProcessDefinitionName(undefined); setKeyword(''); setActiveTab('form');
    listQueryGenerationRef.current += 1; loadingNextPageRef.current = false;
    detailBodyRef.current?.scrollTo({ top: 0 });
  };

  const scrollToSection = (key: DetailTabKey) => {
    const target = key === 'form' ? formRef.current
      : key === 'approval' ? approvalRef.current : commentRef.current;
    if (!target || !detailBodyRef.current) return;
    setActiveTab(key);
    detailBodyRef.current.scrollTo({ top: target.offsetTop - 54, behavior: 'smooth' });
  };

  const syncTabWithScroll = () => {
    const container = detailBodyRef.current;
    if (!container) return;
    const marker = container.scrollTop + 96;
    const sections: Array<{ key: DetailTabKey; element: HTMLElement | null }> = [
      { key: 'form', element: formRef.current },
      { key: 'approval', element: approvalRef.current },
      { key: 'comment', element: commentRef.current },
    ];
    let nextTab: DetailTabKey = 'form';
    sections.forEach(section => {
      if (section.element && section.element.offsetTop <= marker) nextTab = section.key;
    });
    setActiveTab(nextTab);
  };

  const addComment = async () => {
    const content = commentValue.trim();
    if (!activeTask || !activeDetail || !content || commentExecutingRef.current) {
      if (!content) message.warning('请输入评论内容');
      return;
    }
    const taskId = activeTask.taskId;
    const category = activeTask.category === 'initiated' ? 'initiated' : 'work-item';
    const selection = preparedRef.current;
    const execution = {};
    commentExecutingRef.current = execution;
    const prior = commentSubmissionRef.current;
    const key = prior?.taskId === taskId && prior.content === content
      && prior.parentCommentId === replyTargetId ? prior.key : crypto.randomUUID();
    commentSubmissionRef.current = { taskId, content, parentCommentId: replyTargetId, key };
    setCommentSubmitting(true);
    try {
      await myTasksApi.comment(taskId, content,
        { category, parentCommentId: replyTargetId, idempotencyKey: key });
      if (activeTaskIdRef.current !== taskId || preparedRef.current !== selection) return;
      commentSubmissionRef.current = undefined;
      setCommentValue('');
      setReplyTargetId(undefined);
      setMobileCommentIntent(undefined); mobileCommentIntentRef.current = undefined;
      if (category === 'initiated') {
        const nextTotal = (instanceComments?.total ?? initiatedDetail?.commentCount ?? 0) + 1;
        setDetail(current => current && current.summary.taskId === taskId
          && 'commentCount' in current
          ? { ...current, commentCount: Math.max(current.commentCount, nextTotal) } : current);
        await loadInstanceComments(taskId,
          Math.max(1, Math.ceil(nextTotal / INSTANCE_COMMENTS_PAGE_SIZE)));
      } else await loadDetail(taskId);
      message.success('评论已添加');
    } catch (cause) {
      if (activeTaskIdRef.current === taskId && preparedRef.current === selection) message.error(errorMessage(cause));
    } finally {
      if (commentExecutingRef.current === execution) {
        commentExecutingRef.current = undefined;
        if (activeTaskIdRef.current === taskId) setCommentSubmitting(false);
      }
    }
  };

  const deleteComment = async (comment: PortalTaskComment) => {
    if (!activeTask || !comment.deletable) return;
    const taskId = activeTask.taskId;
    const category = activeTask.category === 'initiated' ? 'initiated' : 'work-item';
    try {
      await myTasksApi.deleteComment(taskId, comment.commentId, { category });
      if (activeTaskIdRef.current !== taskId) return;
      if (replyTargetId === comment.commentId) setReplyTargetId(undefined);
      if (category === 'initiated') {
        await loadInstanceComments(taskId, instanceComments?.page ?? 1);
      } else await loadDetail(taskId);
      message.success('评论已删除');
    } catch (cause) {
      if (activeTaskIdRef.current === taskId) message.error(errorMessage(cause));
    }
  };

  const ensureActionGate = (action: PortalWorkItemAction) => {
    if (!activeTask || !activeDetail
        || activeDetail.summary.taskId !== activeTask.taskId
        || !actionHostReady || !activeDetail.flowPageContext) {
      throw new Error('来源表单尚未 READY，当前不能提交任务动作');
    }
    if (isBuiltInTenantApplication(activeDetail)) {
      throw new Error('内建 ADMIN 幂等保存能力待接入，正式流程动作暂不可用');
    }
    const allowedAction = activeDetail.flowPageContext?.allowedActions.find(candidate =>
      candidate.actionCode === action.actionCode
      && candidate.operationType === 'WORKFLOW');
    const expectedPolicy = ['SUBMIT', 'APPROVE'].includes(action.actionCode) ? 'REQUIRED'
      : ['RETURN', 'RECALL', 'END', 'TRANSFER'].includes(action.actionCode) ? 'SKIP' : undefined;
    if (!allowedAction || !expectedPolicy || allowedAction.formValidationPolicy !== expectedPolicy
        || activeTask.category !== 'todo' || activeDetail.flowPageContext.recipientType !== 'TODO') {
      throw new Error('当前动作不属于来源页面的冻结允许动作');
    }
    const host = workItemRuntime?.status === 'ready'
      && workItemRuntime.taskId === activeTask.taskId
      ? workItemRuntime : undefined;
    if (!host) throw new Error('来源表单挂载已过期，请重新加载');
    const key = `${host.mountId}:${activeTask.taskId}:${activeTask.runtimeTaskVersion}`;
    if (actionGateRef.current?.key === key) {
      return {
        gate: actionGateRef.current.gate,
        formValidationPolicy: allowedAction.formValidationPolicy,
      };
    }
    disposeActionGate();
    const gate = new WorkItemActionGate({
      host,
      expectedTaskVersion: activeTask.runtimeTaskVersion,
      callCore: (request, signal) => myTasksApi.action(activeTask.taskId, {
        ...request,
        opinionAttachmentPublicIds: [...request.opinionAttachmentPublicIds],
      }, { signal }),
      isAmbiguousCoreFailure: isAmbiguousRequestFailure,
      isCurrent: () => sessionManager.getSnapshot().activeSession?.status === 'READY'
        && sessionManager.getSnapshot().activeSession?.token.sessionId === host.token?.sessionId,
      runHostOperation: operation => {
        if (!host.token) throw new Error('当前表单会话身份已失效');
        return sessionManager.runHostOperation(host.token, (_snapshot, signal) => operation(signal));
      },
      setUncertain: value => {
        if (host.token && sessionManager.getSnapshot().activeSession?.token.sessionId === host.token.sessionId)
          sessionManager.setUncertain(host.token, value);
      },
    });
    actionGateRef.current = { key, gate };
    return { gate, formValidationPolicy: allowedAction.formValidationPolicy };
  };

  const createPendingAction = (
    action: PortalWorkItemAction,
    formValidationPolicy: 'REQUIRED' | 'SKIP',
    validationGrant: WorkItemActionValidationGrant | undefined,
    opensDialog: boolean,
  ): PendingWorkflowAction => {
    if (!activeTask || !activeDetail
        || activeDetail.summary.taskId !== activeTask.taskId
        || workItemRuntime?.status !== 'ready'
        || workItemRuntime.taskId !== activeTask.taskId) {
      throw new Error('来源表单尚未 READY，当前不能创建任务动作');
    }
    return Object.freeze({
      action,
      taskId: activeTask.taskId,
      detailTaskId: activeDetail.summary.taskId,
      taskVersion: activeTask.runtimeTaskVersion,
      mountId: workItemRuntime.mountId,
      formValidationPolicy,
      validationGrant,
      opensDialog,
    });
  };

  const saveLocal = useCallback((token: FormSessionToken): Promise<void> => {
    const runtime = workItemRuntimeRef.current;
    const prepared = preparedRef.current;
    if (!runtime || runtime.status !== 'ready' || runtime.token?.sessionId !== token.sessionId
        || !prepared || !hasLocalWorkItemSave(prepared.detail)) {
      return Promise.reject(new Error('当前表单没有合法本地保存操作'));
    }
    const currentBridge = sessionManager.getBridge(token);
    if (currentBridge?.contextType !== 'WORK_ITEM') return Promise.reject(new Error('当前表单保存能力已失效'));
    let attempt = localSaveAttemptRef.current;
    if (attempt?.sessionId !== token.sessionId) {
      attempt = { sessionId: token.sessionId, operationId: crypto.randomUUID(), traceId: crypto.randomUUID() };
      localSaveAttemptRef.current = attempt;
    }
    if (attempt.running) return attempt.running;
    const owned = attempt;
    const running = currentBridge.bridge.save({ actionCode: 'SAVE', operationId: owned.operationId, traceId: owned.traceId })
      .then(() => {
        sessionManager.getSessionSnapshot(token); // Recheck ownership before feedback or granting leave.
        sessionManager.setUncertain(token, false);
        if (localSaveAttemptRef.current === owned) localSaveAttemptRef.current = undefined;
      }).catch(error => {
        if (sessionManager.getSnapshot().activeSession?.token.sessionId === token.sessionId) {
          if (isAmbiguousRequestFailure(error)) sessionManager.setUncertain(token, true);
          else if (localSaveAttemptRef.current === owned) localSaveAttemptRef.current = undefined;
        }
        throw error;
      });
    owned.running = running;
    void running.then(() => { owned.running = undefined; }, () => { owned.running = undefined; });
    return running;
  }, [sessionManager]);

  useLayoutEffect(() => {
    sessionManager.setSaveBeforeLeave(saveLocal);
    return () => sessionManager.setSaveBeforeLeave(undefined);
  }, [sessionManager, saveLocal]);

  const openAction = async (action: PortalWorkItemAction) => {
    if (!activeTask || actionDisabled(action)) return;
    if (action.operationType === 'SHELL_LOCAL') {
      if (action.actionCode !== 'SAVE') {
        message.error(`当前页面未注册${workItemActionLabel(action)}处理器`);
        return;
      }
      if (!builtInForm) {
        if (!externalLocalSaveReady || workItemRuntime?.status !== 'ready'
            || !workItemRuntime.bridge) {
          message.error('来源表单尚未注册本地保存能力');
          return;
        }
        const token = workItemRuntime.token;
        if (!token) return;
        setActionSubmitting(true);
        try {
          await saveLocal(token);
          message.success(`${workItemActionLabel(action)}成功`);
        } catch (cause) {
          if (sessionManager.getSnapshot().activeSession?.token.sessionId === token.sessionId)
            message.error(sourceSaveErrorMessage(cause));
        }
        finally {
          if (sessionManager.getSnapshot().activeSession?.token.sessionId === token.sessionId) setActionSubmitting(false);
        }
        return;
      }
      if (!builtInLocalSaveReady || !tenantApplicationFormRef.current) {
        message.error('中台租户申请表单尚未 READY');
        return;
      }
      setActionSubmitting(true);
      try {
        await tenantApplicationFormRef.current.save();
        message.success(`${workItemActionLabel(action)}成功`);
      } catch (cause) { message.error(errorMessage(cause)); }
      finally { setActionSubmitting(false); }
      return;
    }
    if (builtInForm) {
      message.error('内建 ADMIN 幂等保存能力待接入，正式流程动作暂不可用');
      return;
    }
    if (!actionHostReady) {
      message.error('来源表单尚未 READY，当前不能提交任务动作');
      return;
    }
    if (actionOpeningRef.current) return;
    const opening = {};
    actionOpeningRef.current = opening;
    let intent: PendingWorkflowAction | undefined;
    try {
      const actionGate = ensureActionGate(action);
      const opensDialog = workflowActionNeedsDialog(action);
      const identity = createPendingAction(
        action, actionGate.formValidationPolicy, undefined, opensDialog,
      );
      setActionSubmitting(true);
      const validationGrant = await actionGate.gate.validate(
        action, actionGate.formValidationPolicy,
      );
      if (!isCurrentActionIdentity(identity)) return;
      intent = createPendingAction(
        action, actionGate.formValidationPolicy, validationGrant, opensDialog,
      );
    } catch (cause) {
      if (intent === undefined && activeTaskIdRef.current === activeTask.taskId) {
        message.error(workItemActionErrorMessage(cause));
      }
      return;
    } finally {
      if (actionOpeningRef.current === opening) {
        actionOpeningRef.current = undefined;
        setActionSubmitting(false);
      }
    }
    if (!intent) return;
    if (!intent.opensDialog) {
      await executeAction(intent);
      return;
    }
    clearPendingWorkflowAction();
    setPendingWorkflowAction(intent);
    const retainedInput = retainedActionInputRef.current;
    if (retainedInput?.taskId === intent.taskId && retainedInput.code === action.actionCode) {
      setActionOpinion(retainedInput.opinion); setTargetUserId(retainedInput.target);
    }
    retainedActionInputRef.current = undefined;
    if (action.actionCode === 'TRANSFER') {
      void searchTransferTargets('');
    }
  };

  const executeAction = async (
    intent = pendingAction,
    opinion = actionOpinion.trim(),
    target = targetUserId,
  ) => {
    if (!intent || !isCurrentActionIdentity(intent) || actionExecutingRef.current
        || attachmentUploadRef.current || sourceBusy) return;
    if (intent.opensDialog && pendingActionRef.current !== intent) return;
    const action = intent.action;
    if (action.actionCode === 'SAVE') return;
    if (action.requiresComment && !opinion) {
      message.warning('该操作必须填写处理意见'); return;
    }
    if (action.actionCode === 'TRANSFER' && !target) {
      message.warning('请选择转交接收人'); return;
    }
    if (action.actionCode === 'TRANSFER' && !transferTargets.some(user => user.userId === target)) {
      message.warning('转交候选已变化，请重新选择接收人'); return;
    }
    setActionSubmitting(true);
    actionExecutingRef.current = intent;
    try {
      const actionGate = ensureActionGate(action);
      if (actionGate.formValidationPolicy !== intent.formValidationPolicy) {
        throw new Error('当前 Workflow 动作完整性校验策略已变化');
      }
      await actionGate.gate.execute({
        action,
        formValidationPolicy: intent.formValidationPolicy,
        opinion: opinion || undefined,
        targetUserPublicId: target,
        opinionAttachmentPublicIds: actionAttachments.map(
          attachment => attachment.fileAssetPublicId,
        ),
      }, intent.validationGrant);
      if (!isCurrentActionIdentity(intent)) return;
      clearPendingWorkflowAction();
      retainedActionInputRef.current = undefined;
      disposeActionGate();
      await props.onNavigate(buildMyTasksRoute(activeGroup));
      await Promise.all([loadCounts(), loadList()]);
      message.success(`任务已${workItemActionLabel(action)}`);
    } catch (cause) {
      if (!isCurrentActionIdentity(intent)) return;
      if (cause instanceof ApiError && [401, 403, 404, 409].includes(cause.status)) {
        retainedActionInputRef.current = { taskId: intent.taskId, code: action.actionCode, opinion, target };
        message.error(workItemActionErrorMessage(cause));
        await loadDetail(intent.taskId, false, intent);
        return;
      }
      message.error(workItemActionErrorMessage(cause));
    }
    finally {
      if (actionExecutingRef.current === intent) actionExecutingRef.current = undefined;
      if (isCurrentActionIdentity(intent)) setActionSubmitting(false);
    }
  };

  const uploadActionAttachments = async (files: File[] | null) => {
    const intent = pendingAction;
    if (!activeTask || !intent || !isCurrentActionIdentity(intent) || !files?.length
        || attachmentUploadRef.current || actionExecutingRef.current || sourceBusy || sourceUncertain) return;
    const action = intent.action;
    const remaining = 20 - actionAttachmentsRef.current.length;
    if (remaining <= 0) {
      message.warning('每次处理最多上传 20 个附件'); return;
    }
    if (files.length > remaining) { message.warning('每次处理最多上传 20 个附件'); return; }
    const selected = files;
    if (selected.some(file => file.size <= 0 || file.size > 20 * 1024 * 1024)) {
      message.warning('单个附件大小必须在 20MB 以内'); return;
    }
    setAttachmentUploading(true);
    attachmentUploadRef.current = intent;
    const uploaded: ActionAttachment[] = [];
    try {
      const token = workItemRuntimeRef.current?.token;
      if (!token) throw new Error('当前表单会话身份已失效');
      await sessionManager.runHostOperation(token, async (_snapshot, signal) => {
        for (const file of selected) {
          if (signal.aborted) throw new DOMException('附件上传上下文已过期', 'AbortError');
          const attachment = await myTasksApi.uploadAttachment(
            intent.taskId, action.actionCode, intent.taskVersion, file);
          if (signal.aborted || !isCurrentActionIdentity(intent) || pendingActionRef.current !== intent)
            throw new DOMException('附件上传上下文已过期', 'AbortError');
          if (!attachment.fileAssetPublicId || uploaded.some(item => item.fileAssetPublicId === attachment.fileAssetPublicId)
              || actionAttachmentsRef.current.some(item => item.fileAssetPublicId === attachment.fileAssetPublicId)) {
            throw new Error('附件确认标识重复或无效，请重新上传');
          }
          uploaded.push({ ...attachment, file, previewUrl: URL.createObjectURL(file) });
        }
      });
      if (!isCurrentActionIdentity(intent) || pendingActionRef.current !== intent) {
        uploaded.forEach(attachment => URL.revokeObjectURL(attachment.previewUrl));
        return;
      }
      setActionAttachments(current => [...current, ...uploaded]);
    } catch (cause) {
      uploaded.forEach(attachment => URL.revokeObjectURL(attachment.previewUrl));
      if (isCurrentActionIdentity(intent)) message.error(errorMessage(cause));
    } finally {
      if (attachmentUploadRef.current === intent) attachmentUploadRef.current = undefined;
      if (isCurrentActionIdentity(intent)
          && pendingActionRef.current === intent) {
        setAttachmentUploading(false);
        if (attachmentInputRef.current) attachmentInputRef.current.value = '';
      }
    }
  };

  const searchTransferTargets = async (value: string) => {
    const intent = pendingActionRef.current;
    if (!intent || !isCurrentActionIdentity(intent)) return;
    const generation = ++transferQueryRef.current;
    try {
      const targets = await myTasksApi.transferTargets(value);
      if (generation === transferQueryRef.current && pendingActionRef.current === intent
          && isCurrentActionIdentity(intent)) setTransferTargets(targets);
    } catch (cause) {
      if (generation === transferQueryRef.current && pendingActionRef.current === intent
          && isCurrentActionIdentity(intent)) message.error(errorMessage(cause));
    }
  };

  const markCcRead = async (taskId?: string) => {
    if (readAttemptRef.current || taskId && (activeTask?.category !== 'cc' || activeTaskIdRef.current !== taskId)) return;
    readAttemptRef.current = true; setReadBusy(true);
    try {
      if (taskId) {
        await myTasksApi.markRead(taskId);
        applyCcRead(taskId);
      } else {
        await myTasksApi.markAllCcRead();
        setTasks(current => current.map(task => task.category === 'cc' ? { ...task, unread: false, status: '抄送已读' } : task));
      }
      await loadCounts();
    } catch (cause) { message.error(errorMessage(cause)); }
    finally { readAttemptRef.current = false; setReadBusy(false); }
  };

  const sourceForm = activeDetail && activeTask ? activeDetail.flowPageContext === null
    ? activeDetail.pageRoute ? <InitiationViewFormHost key={activeDetail.summary.taskId}
        detail={activeDetail} applications={props.applications} token={displayedSelection?.token}
        sourceFailure={displayedSelection?.sourceFailure} onRetry={() => void loadDetail(activeDetail.summary.taskId)}
        identity={props.identity} onSessionExpired={props.onSessionExpired} />
      : <Alert type="info" title="来源应用未提供启动表单专页" />
    : isBuiltInTenantApplication(activeDetail) ? <>
        <Alert type="warning" title="正式流程动作暂不可用" content="内建 ADMIN 幂等保存能力待接入；当前仅允许本地保存，不会继续提交 Workflow 动作。" />
        <TenantApplicationEditPage ref={tenantApplicationFormRef} task={activeTask} onRuntimeStatusChange={handleBuiltInRuntimeChange} />
      </> : <WorkItemFormHost key={activeDetail.summary.taskId} detail={activeDetail} applications={props.applications}
        token={displayedSelection?.token} sourceFailure={displayedSelection?.sourceFailure}
        onRetry={() => void loadDetail(activeDetail.summary.taskId)} identity={props.identity}
        onRuntimeChange={handleWorkItemRuntimeChange} onSessionExpired={props.onSessionExpired} /> : null;

  if (props.mobile) {
    const mobileTask = activeTask && activeDetail ? taskModel(activeTask, activeDetail, visibleComments) : undefined;
    if (mobileTask && activeTask?.category === 'todo') mobileTask.actions = activeDetail!.availableActions.map(action => ({
      code: action.actionCode, label: workItemActionLabel(action), requiresOpinion: action.requiresComment,
      disabled: actionDisabled(action),
    }));
    const parent = visibleComments.find(comment => comment.commentId === replyTargetId);
    return mobileTask ? <MobileTaskDetailView key={mobileTask.id} task={mobileTask} sourceForm={sourceForm}
      onBack={() => void props.onNavigate(buildMyTasksRoute(activeGroup))}
      onMarkCcRead={id => void markCcRead(id)} readBusy={readBusy}
      onOpenComment={target => {
        if (commentExecutingRef.current) return;
        const intent = ++nextMobileCommentIntentRef.current;
        mobileCommentIntentRef.current = intent; setMobileCommentIntent(intent);
        setReplyTargetId(target?.id); commentSubmissionRef.current = undefined;
      }}
      onAction={code => { const action = activeDetail?.availableActions.find(candidate => candidate.actionCode === code); if (action) void openAction(action); }}
      commentTotal={commentTotal} commentsLoading={commentsLoading} commentsError={commentsError}
      commentsPage={instanceComments?.page ?? 1} commentsPages={initiatedDetail ? Math.ceil(commentTotal / INSTANCE_COMMENTS_PAGE_SIZE) : 1}
      onCommentsPage={page => void loadInstanceComments(mobileTask.id, page)} navigationMessage={props.navigationLoadingMessage}
      actionSheet={pendingAction ? <MobileTaskActionPage key={`${pendingAction.mountId}:${pendingAction.action.actionCode}`}
        action={{ code: pendingAction.action.actionCode, label: workItemActionLabel(pendingAction.action), requiresOpinion: pendingAction.action.requiresComment }}
        opinion={actionOpinion} onOpinionChange={setActionOpinion} targetUserId={targetUserId} onTargetChange={setTargetUserId}
        candidates={transferTargets} onSearchTargets={value => void searchTransferTargets(value)}
        busy={actionSubmitting || sourceBusy} editingDisabled={sourceUncertain} uploading={attachmentUploading}
        attachments={actionAttachments} onAddFiles={uploadActionAttachments}
        onRemoveAttachment={id => setActionAttachments(current => current.filter(item => { if (item.fileAssetPublicId !== id) return true; URL.revokeObjectURL(item.previewUrl); return false; }))}
        onClose={() => {
          const current = sessionManager.getSnapshot().activeSession;
          if (pendingActionRef.current !== pendingAction || !isCurrentActionIdentity(pendingAction)
              || actionOpeningRef.current || actionExecutingRef.current || attachmentUploadRef.current
              || current?.hostPending || current?.state?.busy || current?.uncertain || current?.state?.uncertain) return false;
          retainedActionInputRef.current = undefined; clearPendingWorkflowAction(); return true;
        }}
        onSubmit={() => executeAction(pendingAction)} /> : null}
      commentSheet={mobileCommentIntent !== undefined ? <MobileTaskCommentSheet key={`${mobileTask.id}:${mobileCommentIntent}`} replyTo={replyTargetId ? { id: replyTargetId, author: parent?.author ?? '较早评论', content: parent?.content ?? '原评论暂不可见' } : null}
        content={commentValue} onContentChange={setCommentValue} busy={commentSubmitting}
        onClose={() => {
          if (mobileCommentIntentRef.current !== mobileCommentIntent || commentExecutingRef.current) return false;
          mobileCommentIntentRef.current = undefined; setMobileCommentIntent(undefined); return true;
        }} onSubmit={() => mobileCommentIntentRef.current === mobileCommentIntent ? addComment() : Promise.resolve()} /> : null} />
      : <MobileTasksView tasks={tasks.map(task => taskModel(task))} group={activeGroup} counts={counts}
        keyword={keyword} onKeywordChange={setKeyword} workflowName={processDefinitionName ?? 'all'}
        workflowNames={processDefinitionNames} onWorkflowNameChange={value => setProcessDefinitionName(value === 'all' ? undefined : value)}
        total={listTotal} loading={listLoading || detailLoading || !!props.navigationLoadingMessage} error={listError}
        hasMore={hasMoreTasks} markingRead={readBusy} onGroupChange={group => void selectGroup(group)}
        onOpenTask={id => void selectTask(id)} onMarkAllCcRead={() => void markCcRead()}
        onRetry={() => void loadList(1, false)} onLoadMore={loadNextPage} />;
  }

  return <div className="portal-my-tasks">
    <nav className="portal-task-group-nav" aria-label="任务分类">
      {taskGroups.map(group => {
        const count = counts[group.key];
        return <button className={activeGroup === group.key ? 'active' : ''} key={group.key}
          type="button" onClick={() => selectGroup(group.key)}>
          <span className="portal-task-group-nav__icon">{taskGroupIcon(group.key)}</span>
          <span className="portal-task-group-nav__label">
            {group.label}{count > 0 ? <em>{count}</em> : null}
          </span>
        </button>;
      })}
    </nav>

    <aside className="portal-approval-list">
      <div className="portal-approval-list__toolbar">
        <Select showSearch allowClear value={processDefinitionName}
          placeholder="按流程定义名称搜索" options={processDefinitionNames.map(name => ({
            label: name, value: name,
          }))} onChange={value => setProcessDefinitionName(value || undefined)} />
      </div>
      <div className="portal-approval-list__cards" onScroll={handleTaskListScroll}>
        {listLoading && tasks.length === 0
          ? <div className="portal-task-list-loading"><Spin loading tip="正在加载任务" /></div> : null}
        {listError ? (
          <div className="ui-state-page ui-state-page--compact portal-approval-list__empty">
            <strong>任务加载失败</strong><Text type="secondary">{listError}</Text>
            <Button size="small" onClick={() => void loadList(1, false)}>重新加载</Button>
          </div>
        ) : null}
        {!listError ? tasks.map(task => (
          <button className={[
            'ui-task-card', 'ui-task-card--oa', 'portal-approval-card',
            task.taskId === activeTask?.taskId ? 'portal-approval-card--active' : '',
            task.unread ? 'portal-approval-card--unread' : '',
          ].filter(Boolean).join(' ')} key={task.taskId} type="button"
            onClick={() => selectTask(task.taskId)}>
            <span className="ui-task-card__head">
              <strong className="ui-task-card__title">{task.processDefinitionName}</strong>
              <span className="portal-approval-card__status-group">
                {task.urgent ? <Tag color="red">催办</Tag> : null}
                <span className="ui-task-card__status">{task.status}</span>
              </span>
            </span>
            <span className="portal-task-card__node-name">{task.taskName}</span>
            <span className="ui-task-card__fields">
              {task.summaryItems.slice(0, 4).map(field => (
                <span className="ui-task-card__field" key={`${task.taskId}-${field.label}`}>
                  <span>{field.label}：</span><span>{field.value}</span>
                </span>
              ))}
            </span>
            <span className="ui-task-card__meta">
              <UserChip name={task.initiator} avatarUrl={task.initiatorAvatarUrl} />
              <span className="portal-task-card__time">{taskCardTime(task)}</span>
            </span>
          </button>
        )) : null}
        {!listLoading && !listError && tasks.length === 0 ? (
          <div className="ui-state-page ui-state-page--compact portal-approval-list__empty portal-task-empty-state">
            <img className="ui-state-page__illustration" src={emptyListIllustration} alt="" />
            <strong>当前分类暂无任务</strong>
            <Text type="secondary">可以切换左侧任务导航查看其他内容。</Text>
          </div>
        ) : null}
        {listLoading && tasks.length > 0 ? (
          <div className="portal-task-list-load-more"><Spin loading tip="正在加载更多" /></div>
        ) : null}
        {!listLoading && !listError && tasks.length > 0 && !hasMoreTasks ? (
          <div className="portal-task-list-end"><Text type="secondary">暂无更多数据</Text></div>
        ) : null}
      </div>
    </aside>

    <div className="portal-approval-detail" aria-busy={!!props.navigationLoadingMessage || detailLoading}>
      {props.navigationLoadingMessage ? <div className="portal-form-navigation-overlay"
        role="status" aria-live="polite">
        <Spin loading tip={props.navigationLoadingMessage} />
      </div> : null}
      {activeTask ? <>
        <header className="ui-detail-header portal-approval-detail__header">
          <div className="ui-detail-header__main">
            <div className="ui-detail-header__content">
              <div className="ui-detail-header__title-row">
                <strong>{activeTask.title}</strong>
                <span className="portal-task-detail__status-group">
                  <Tag color={activeTask.urgent ? 'red' : 'arcoblue'}>{activeTask.status}</Tag>
                </span>
              </div>
              <div className="ui-detail-header__meta portal-approval-detail__meta">
                <span className="portal-approval-detail__meta-item">
                  <span>发起人：</span><UserChip
                    name={activeTask.initiator}
                    avatarUrl={activeTask.initiatorAvatarUrl}
                  />
                </span>
                <span className="portal-approval-detail__meta-item">
                  提交于：{formatShanghai(activeDetail?.traceNodes[0]?.records[0]?.handledAt
                    ?? activeDetail?.traceNodes[0]?.startedAt ?? activeTask.receivedAt)}
                </span>
                <span className="portal-approval-detail__meta-item">
                  来源：{sourceSystemName(activeTask.sourceSystem)}
                </span>
                <span className="portal-approval-detail__meta-item">来源状态：{activeTask.sourceState}</span>
              </div>
            </div>
          </div>
        </header>

        <nav className="portal-approval-detail__tabs" aria-label="任务详情章节">
          {detailTabs.map(tab => (
            <button className={activeTab === tab.key ? 'active' : ''} key={tab.key}
              type="button" onClick={() => scrollToSection(tab.key)}>{tab.label}</button>
          ))}
        </nav>

        <div className="portal-approval-detail__scroll" ref={detailBodyRef} onScroll={syncTabWithScroll}>
          {detailLoading ? <div className="portal-task-detail-loading"><Spin loading tip="正在加载任务详情" /></div> : null}
          {!detailLoading && activeDetail ? <>
            <section className="portal-approval-detail__section" ref={formRef}>
              <div className="portal-approval-detail__section-title">
                <div><span>01</span><strong>{activeTask.category === 'initiated'
                  ? '流程表单' : '表单详情'}</strong></div>
              </div>
              {sourceForm}
              {activeDetail.attachments.length > 0 ? (
                <div className="ui-description-panel ui-description-panel--page ui-description-panel--readonly">
                <div className="ui-description-panel__body">
                  <div className="ui-description-panel__section">
                    <div className="ui-description-panel__section-title">附件信息</div>
                    <AttachmentBox attachments={activeDetail.attachments} />
                  </div>
                </div>
              </div>) : null}
            </section>

            <section className="portal-approval-detail__section" ref={approvalRef}>
              <div className="portal-approval-detail__section-title">
                <div><span>02</span><strong>流程详情</strong></div>
                <Text type="secondary">流程节点与处理意见</Text>
              </div>
              {activeDetail.traceNodes.length > 0 ? (
                <div className="ui-process-timeline">
                  {activeDetail.traceNodes.map(node => {
                    const nodeLabel = traceNodeLabel(node);
                    const tone = traceIndicatorTone(nodeLabel);
                    return (
                    <div className={`ui-process-timeline__item ui-process-timeline__item--${node.status} portal-process-timeline__item--${tone}`}
                      key={node.nodeId}>
                      <span className="ui-process-timeline__dot" />
                      <div className="ui-process-timeline__content">
                        <div className="ui-process-timeline__title-row">
                          <strong>{node.nodeName}</strong>
                          <Tag color={traceIndicatorColor(tone, node.status)}>
                            {nodeLabel}
                          </Tag>
                        </div>
                        <div className="ui-process-timeline__records">
                          {visibleTraceRecords(node).map(({ record, read }, recordIndex) => (
                          <div className="ui-process-timeline__record"
                            key={record.operationId ?? record.participantId ?? `${node.nodeId}-${recordIndex}`}>
                            <div className="ui-process-timeline__record-head">
                              <div className="portal-process-timeline__actor">
                                <UserChip name={record.actor} avatarUrl={record.actorAvatarUrl} />
                                {read ? <span className="portal-process-timeline__read">已读</span> : null}
                              </div>
                              <span className="ui-process-timeline__record-meta">
                                {formatShanghai(record.handledAt)}
                              </span>
                            </div>
                            {record.opinion ? <p className="ui-process-timeline__text">{record.opinion}</p> : null}
                            {record.attachments.length ? (
                              <div className="ui-process-timeline__attachments">
                                {record.attachments.map(attachment => (
                                  <div className="ui-process-timeline__attachment portal-process-attachment"
                                    key={attachment.fileAssetPublicId}>
                                    <span className={`ui-file-icon ui-file-icon--${attachment.type}`}>
                                      <img src={fileIconAssets[attachment.type]} alt="" />
                                    </span>
                                    <span className="portal-process-attachment__name">{attachment.name}</span>
                                    <small>{attachment.size}</small>
                                    <AttachmentActions attachment={attachment} />
                                  </div>
                                ))}
                              </div>
                            ) : null}
                          </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              ) : (
                <div className="portal-approval-detail__no-process">
                  <IconRefresh />
                  <div><strong>暂无任务轨迹</strong><Text type="secondary">当前任务未提供流程节点或状态变化数据。</Text></div>
                </div>
              )}
            </section>

            <section className="portal-approval-detail__section" ref={commentRef}>
              <div className="portal-approval-detail__section-title">
                <div><span>03</span><strong>评论</strong></div>
                <Text type="secondary">{commentTotal} 条</Text>
              </div>
              <div className="portal-comment-list">
                {visibleComments.map(comment => {
                  const parent = visibleComments.find(candidate =>
                    candidate.commentId === comment.parentCommentId);
                  return (
                  <article className="portal-comment" key={comment.commentId}>
                    <div className="portal-comment__head">
                      <UserChip name={comment.author} avatarUrl={comment.authorAvatarUrl} />
                      <span className="portal-comment__time">{formatShanghai(comment.createdAt)}</span>
                    </div>
                    <div className="portal-comment__body">
                      {comment.parentCommentId ? <div className="portal-comment__reply-reference">
                        <Text type="secondary">
                          回复 {parent?.author ?? comment.replyTo ?? '较早评论'}
                        </Text>
                        <blockquote className="portal-comment__quote">
                          {comment.parentDeleted ? '评论已删除'
                            : parent?.content ?? comment.parentContent ?? '原评论暂不可见'}
                        </blockquote>
                      </div> : null}
                      <p>{comment.content}</p>
                      <Button type="text" size="mini" onClick={() => {
                        setReplyTargetId(comment.commentId);
                        commentSubmissionRef.current = undefined;
                      }}>回复</Button>
                      {comment.deletable ? <Button type="text" size="mini" status="danger"
                        onClick={() => Modal.confirm({
                          title: '删除评论',
                          content: '删除后无法恢复，已有回复将显示“评论已删除”。',
                          okButtonProps: { status: 'danger' },
                          onOk: () => deleteComment(comment),
                        })}>删除</Button> : null}
                    </div>
                  </article>
                ); })}
                {visibleComments.length === 0 && commentTotal === 0 && !commentsLoading
                  ? <Text type="secondary">暂无评论，欢迎补充协作信息。</Text> : null}
              </div>
              {commentsError ? <Alert type="error" content={commentsError}
                action={<Button size="mini" onClick={() => void loadInstanceComments(
                  activeTask.taskId, instanceComments?.page ?? 1)}>重试</Button>} /> : null}
              {initiatedDetail && commentTotal > INSTANCE_COMMENTS_PAGE_SIZE
                ? <div className="portal-comment-pagination">
                  <Button size="mini" disabled={commentsLoading || (instanceComments?.page ?? 1) <= 1}
                    onClick={() => void loadInstanceComments(activeTask.taskId,
                      (instanceComments?.page ?? 1) - 1)}>上一页</Button>
                  <span>{instanceComments?.page ?? 1} / {Math.ceil(commentTotal / INSTANCE_COMMENTS_PAGE_SIZE)}</span>
                  <Button size="mini" disabled={commentsLoading
                    || (instanceComments?.page ?? 1) >= Math.ceil(commentTotal / INSTANCE_COMMENTS_PAGE_SIZE)}
                    onClick={() => void loadInstanceComments(activeTask.taskId,
                      (instanceComments?.page ?? 1) + 1)}>下一页</Button>
                </div> : null}
              {(activeDetail.flowPageContext || initiatedDetail) ? <div className="portal-comment-editor uikit-main">
                {replyTargetId ? <div className="portal-comment-reply-target">
                  <span>正在回复 {visibleComments.find(comment =>
                    comment.commentId === replyTargetId)?.author ?? '较早评论'}</span>
                  <blockquote className="portal-comment__quote">
                    {visibleComments.find(comment => comment.commentId === replyTargetId)?.content
                      ?? '原评论暂不可见'}
                  </blockquote>
                  <Button type="text" size="mini" onClick={() => {
                    setReplyTargetId(undefined);
                    commentSubmissionRef.current = undefined;
                  }}>取消回复</Button>
                </div> : null}
                <Input.TextArea value={commentValue} placeholder="输入评论，评论不会替代审批意见"
                  autoSize={{ minRows: 3, maxRows: 5 }} onChange={setCommentValue} />
                <Button type="primary" icon={<IconMessage />}
                  loading={commentSubmitting} onClick={() => void addComment()}>
                  {replyTargetId ? '发表回复' : '发表评论'}</Button>
              </div> : null}
            </section>
          </> : null}
        </div>

        {activeTask.category !== 'cc' ? <footer className="portal-task-detail-actions">
          {activeDetail?.availableActions.map(action => (
            <Button key={action.actionCode}
              className={action.emphasis === 'warning' ? 'portal-task-action--warning' : undefined}
              type={action.emphasis === 'primary' ? 'primary' : 'secondary'}
              status={action.emphasis === 'danger' || action.emphasis === 'warning'
                ? action.emphasis : undefined}
              icon={taskActionIcon(action)}
              disabled={actionDisabled(action)}
              loading={actionSubmitting
                && (action.actionCode === 'APPROVE' || action.actionCode === 'SAVE')}
              onClick={() => void openAction(action)}>{workItemActionLabel(action)}</Button>
          ))}
        </footer> : null}
      </> : detailLoading ? <div className="portal-task-detail-loading"><Spin loading tip="正在加载任务详情" /></div> : (
        <div className="ui-state-page ui-state-page--block portal-approval-detail__empty portal-task-empty-state">
          <img className="ui-state-page__illustration" src={emptyListIllustration} alt="" />
          <strong>暂无可查看内容</strong>
          <Text type="secondary">当前指标分类没有任务。</Text>
        </div>
      )}
    </div>

    <Modal title={pendingAction?.action.actionCode === 'TRANSFER' ? '转交任务' : '填写处理意见'}
      visible={Boolean(pendingAction)} okText={pendingAction?.action.label ?? '确认'} cancelText="取消"
      confirmLoading={actionSubmitting}
      okButtonProps={{
        disabled: pendingAction?.action.requiresComment && !actionOpinion.trim()
          || pendingAction?.action.actionCode === 'TRANSFER' && !targetUserId,
      }}
      onCancel={() => {
        if (pendingActionRef.current === pendingAction) clearPendingWorkflowAction();
      }} onOk={() => void executeAction(pendingAction)}>
      <div className="portal-task-action-dialog">
        {pendingAction?.action.actionCode === 'TRANSFER' ? <label>
          <span>转交接收人</span>
            <Select value={targetUserId} showSearch allowClear placeholder="搜索并选择接收人"
              onSearch={value => void searchTransferTargets(value)}
            onChange={setTargetUserId}
            options={transferTargets.map(user => ({
              label: `${user.name}${user.organizationName ? ` · ${user.organizationName}` : ''}`,
              value: user.userId,
            }))} />
        </label> : null}
        <label>
          <span className="portal-task-action-dialog__label">
            处理意见
            {pendingAction?.action.requiresComment
              ? <span className="portal-task-action-dialog__required" aria-hidden="true">*</span>
              : null}
          </span>
          <Input.TextArea className="portal-task-action-dialog__opinion"
            aria-required={pendingAction?.action.requiresComment || undefined}
            value={actionOpinion} onChange={setActionOpinion}
            placeholder="请输入本次处理意见" autoSize={{ minRows: 3, maxRows: 5 }} />
        </label>
        {pendingAction?.action.actionCode !== 'TRANSFER' ? (
          <StandardAttachment className="portal-action-attachment-box" mode="edit" variant="plain"
            title={<span className="portal-action-attachment-box__title">
              <strong>附件</strong><small>单个文件不超过 20MB，最多 20 个</small>
            </span>}
            files={actionAttachments.map(attachment => ({
              ...attachment,
              id: attachment.fileAssetPublicId,
              name: attachment.fileName,
              sizeText: `${(attachment.sizeBytes / 1024).toFixed(1)} KB`,
            }))}
            headExtra={<>
              <Button size="small" type="secondary" icon={<IconUpload />}
                loading={attachmentUploading} disabled={attachmentUploading}
                onClick={() => attachmentInputRef.current?.click()}>
                上传附件
              </Button>
              <input ref={attachmentInputRef} type="file" multiple hidden
                onChange={event => void uploadActionAttachments(Array.from(event.target.files ?? []))} />
            </>}
            onDownload={attachment => {
              const link = document.createElement('a');
              link.href = attachment.previewUrl;
              link.download = attachment.fileName;
              link.style.display = 'none';
              document.body.appendChild(link);
              link.click();
              link.remove();
            }}
            onDelete={attachment => setActionAttachments(current => current.filter(item => {
              if (item.fileAssetPublicId !== attachment.fileAssetPublicId) return true;
              URL.revokeObjectURL(item.previewUrl);
              return false;
            }))} />
        ) : null}
      </div>
    </Modal>
  </div>;
}
